<template>
  <div class="tab-panel">
    <div class="bill-item">
      <div class="form-grid">
        <div class="form-group">
          <label>Title:</label>
          <InputText v-model="bicamBill.title" />
        </div>
        <div class="form-group">
          <label>Short Title:</label>
          <InputText v-model="bicamBill.short_title" />
        </div>
      </div>
      <div class="form-grid">
        <div class="form-group">
          <label>Bill Nos.:</label>
          <InputText v-model="bicamBill.nos" />
        </div>
        <div class="form-group">
          <label>Author(s):</label>
          <MultiSelect v-model="bicamBill.authors" :options="authors" placeholder="Select Author" class="w-full" filter />
        </div>
      </div>
      <div class="form-grid">
        <div class="form-group">
          <label>Remarks:</label>
          <Textarea v-model="bicamBill.remarks" rows="2"></Textarea>
        </div>
        <div class="form-group">
          <label>Source/Link:</label>
          <Textarea v-model="bicamBill.source" rows="2"></Textarea>
        </div>
      </div>
      <div class="form-group">
        <label>Salient Features:</label>
        <Textarea v-model="bicamBill.salient_features" rows="2" placeholder="Enter salient features..."></Textarea>
      </div>

      <!-- Legislative Status Section -->
      <div class="subsection">
        <div class="subsection-header">
          <h6>Legislative Status</h6>
          <Button type="button" @click="addBicamLegisStatus()" class="add-sub-btn">Add Legislative Status</button>
        </div>
        <div v-for="(status, sidx) in bicamBill.legisstatus" :key="sidx" class="status-item">
          <div class="status-header">
            <span>Status #{{ sidx + 1 }}</span>
            <Button type="button" @click="deleteBicamLegisStatus(sidx)" class="delete-sub-btn">Delete</button>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>Adopted Version:</label>
              <Select v-model="status.adopted_version" :options="adoptedVersions"/>
            </div>
            <div class="form-group">
              <label>Legislative Status:</label>
              <Select v-model="status.legislative_status" :options="statusBicam"/>
            </div>
            <div class="form-group">
              <label>Date:</label>
              <DatePicker v-model="status.date" @date-select="dateSelect"/>
            </div>
          </div>
        </div>
      </div>

      <!-- Position Papers Section -->
      <PositionPaper 
        :positionPapers="bicamPositionPapers"
        :isDisabled="false"
        :statusOptions="['Bicam Conference Report']"
        @addPositionPaper="() => $emit('addPositionPaper', 'bicam')"
        @deletePositionPaper="(index) => $emit('deletePositionPaper', 'bicam', null, index)"
      />

      <!-- Bicam Key Issues Section -->
      <KeyIssuesSection 
        :keyIssues="keyIssues"
        :activeTab="keyIssuesActive"
        :revenueStartYear="revenueStartYear"
        :revenueEndYear="revenueEndYear"
        :budgetaryStartYear="budgetaryStartYear"
        :budgetaryEndYear="budgetaryEndYear"
        @update:activeTab="keyIssuesActive = $event"
        @update:keyIssues="$emit('update:keyIssues', $event)"
        @addRevenueRow="$emit('addBicamRevenueRow')"
        @addBudgetaryRow="$emit('addBicamBudgetaryRow')"
        @addDofConcernsRow="$emit('addBicamDofConcernsRow')"
        @deleteRevenueRow="$emit('deleteBicamRevenueRow', $event)"
        @deleteBudgetaryRow="$emit('deleteBicamBudgetaryRow', $event)"
        @deleteDofConcernsRow="$emit('deleteBicamDofConcernsRow', $event)"
        @getYearTotal="$emit('getYearTotal', $event)"
        @autoResize="$emit('autoResize', $event)"
        @updateRevenueStartYear="$emit('updateRevenueStartYear', $event)"
        @updateRevenueEndYear="$emit('updateRevenueEndYear', $event)"
        @updateBudgetaryStartYear="$emit('updateBudgetaryStartYear', $event)"
        @updateBudgetaryEndYear="$emit('updateBudgetaryEndYear', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import MultiSelect from 'primevue/multiselect'
import KeyIssuesSection from './KeyIssuesSection.vue'
import PositionPaper from './PositionPaper.vue'

import moment from 'moment-timezone';

moment.tz.setDefault('Asia/Taipei')

const dateSelect = () => {
  Date.prototype.toJSON = function(){ return moment(this).format(); }
    moment().tz("Asia/Taipei").format();
}

const props = defineProps({
  bicamBill: {
    type: Object,
    required: true
  },
  keyIssues: {
    type: Object,
    required: true
  },
  authors: {
    type: Array,
    default: () => []
  },
  revenueStartYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  revenueEndYear: {
    type: Number,
    default: () => new Date().getFullYear() + 5
  },
  budgetaryStartYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  budgetaryEndYear: {
    type: Number,
    default: () => new Date().getFullYear() + 5
  },
  positionPapers: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits([
  'addBicamRevenueRow',
  'addBicamBudgetaryRow',
  'addBicamDofConcernsRow',
  'deleteBicamRevenueRow',
  'deleteBicamBudgetaryRow',
  'deleteBicamDofConcernsRow',
  'getYearTotal',
  'autoResize',
  'addPositionPaper',
  'deletePositionPaper',
  'update:keyIssues',
  'updateRevenueStartYear',
  'updateRevenueEndYear',
  'updateBudgetaryStartYear',
  'updateBudgetaryEndYear'
])

const keyIssuesActive = ref('revenue')

const statusBicam = ref([
  'Adopted version',
  'Pending Bicam Conference',
  'Bicam Meeting on Disagreeing Provisions',
  'Conference Committee Report Approved/Ratified by the Senate',
  'Conference Committee Report Approved/Ratified by the House of Representatives'
])

const adoptedVersions = ref([
  'House',
  'Senate'
])

const bicamPositionPapers = computed(() => {
  return props.positionPapers.filter(p => p.type === 'bicam')
})

const addBicamLegisStatus = () => {
  props.bicamBill.legisstatus.push({
    adopted_version: '',
    legislative_status: '',
    date: ''
  })
}

const deleteBicamLegisStatus = (statusIndex) => {
  props.bicamBill.legisstatus.splice(statusIndex, 1)
}


</script>

<style scoped>
.bill-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #059669;
  font-size: 0.9rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
}

.subsection {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.subsection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.subsection-header h6 {
  color: #059669;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
}

.status-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.add-sub-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.add-sub-btn:hover {
  background: linear-gradient(135deg, #047857 0%, #10b981 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.delete-sub-btn {
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.8rem;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
}

.delete-sub-btn:hover {
  background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .subsection-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .status-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>